/**
 * An in-memory storage backend that behaves like the real S3 data bucket's
 * conditional writes (contracts/storage-and-permissions.md), so local
 * development and tests never need an AWS account (quickstart.md Part 1).
 * app/src/store/s3-storage.ts (User Story 3, task T063) implements the
 * exact same ObjectStorage interface for real.
 */

export interface StoredObject<T> {
  value: T;
  /** Opaque version token; compare with ifMatch/ifNoneMatch, never parse it. */
  etag: string;
}

export interface PutOptions {
  /** Succeed only if the object's current etag equals this (the normal save path). */
  ifMatch?: string;
  /** Succeed only if the object does not exist yet (`'*'`; "create only if absent"). */
  ifNoneMatch?: '*';
}

/** Thrown when a conditional write's precondition is not met (someone else changed it first). */
export class ConditionalWriteError extends Error {
  constructor(public readonly path: string) {
    super(`Conditional write failed for "${path}": the object changed since it was read.`);
    this.name = 'ConditionalWriteError';
  }
}

/**
 * Thrown when the permissions refuse a read or write (a member asking for another person's cellar,
 * or credentials that no longer allow it). S3 answers this the same whether or not the object
 * exists, so it must never be read as "missing" (contracts/storage-and-permissions.md).
 */
export class StorageAccessDeniedError extends Error {
  constructor(public readonly path: string) {
    super(`Access denied for "${path}".`);
    this.name = 'StorageAccessDeniedError';
  }
}

export interface ObjectStorage {
  get<T>(path: string): Promise<StoredObject<T> | undefined>;
  put<T>(path: string, value: T, options?: PutOptions): Promise<{ etag: string }>;
  /** Paths starting with `prefix`, as a real S3 prefix listing would return. */
  list(prefix: string): Promise<string[]>;
}

/** `initial` preloads objects (path -> value), e.g. the demo seed for preview deploys. */
export function createFakeStorage(initial: Record<string, unknown> = {}): ObjectStorage {
  const objects = new Map<string, StoredObject<unknown>>();
  let nextEtag = 1;
  for (const [path, value] of Object.entries(initial)) {
    objects.set(path, { value: structuredClone(value), etag: `v${nextEtag++}` });
  }

  return {
    async get<T>(path: string): Promise<StoredObject<T> | undefined> {
      const found = objects.get(path);
      return found ? (structuredClone(found) as StoredObject<T>) : undefined;
    },

    async put<T>(path: string, value: T, options: PutOptions = {}): Promise<{ etag: string }> {
      const existing = objects.get(path);

      if (options.ifNoneMatch === '*' && existing) {
        throw new ConditionalWriteError(path);
      }
      if (options.ifMatch !== undefined && existing?.etag !== options.ifMatch) {
        throw new ConditionalWriteError(path);
      }

      const etag = `v${nextEtag++}`;
      objects.set(path, { value: structuredClone(value), etag });
      return { etag };
    },

    async list(prefix: string): Promise<string[]> {
      return [...objects.keys()].filter((path) => path.startsWith(prefix));
    },
  };
}

/**
 * Label photos (owner request, 2026-10-11): JPEG bytes stored next to the cellar documents, each
 * under a new name, written once and never overwritten (a second write is ConditionalWriteError).
 */
export interface PhotoStorage {
  put(path: string, jpeg: Uint8Array): Promise<void>;
  /** The photo's bytes, or undefined when there is none. */
  get(path: string): Promise<Uint8Array | undefined>;
  /** Deletes the photo (owner request, 2026-10-15); nothing to delete is not an error. */
  delete(path: string): Promise<void>;
}

/** The in-memory photos, for previews and tests: lost on page reload, like the fake storage. */
export function createFakePhotoStorage(): PhotoStorage {
  const photos = new Map<string, Uint8Array>();
  return {
    async put(path, jpeg) {
      if (photos.has(path)) throw new ConditionalWriteError(path);
      photos.set(path, new Uint8Array(jpeg));
    },
    async get(path) {
      const found = photos.get(path);
      return found ? new Uint8Array(found) : undefined;
    },
    async delete(path) {
      photos.delete(path);
    },
  };
}
